import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, ArrowUp, ArrowDown, Upload, Check, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';
import { IconeCategoria } from '../components/CategoryCards';

export default function Categorias() {
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [editando, setEditando] = useState(null);
  const fileRef = useRef(null);
  const [subindo, setSubindo] = useState(false);

  const { data: cats, isLoading } = useQuery({
    queryKey: ['admin-categorias'],
    queryFn: async () => (await apiFetch('/admin/categorias')).json(),
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['admin-categorias'] });
    qc.invalidateQueries({ queryKey: ['catalogo'] });
  };

  const criar = async (e) => {
    e.preventDefault();
    if (nome.trim().length < 2) return;
    setEnviando(true);
    const r = await apiFetch('/admin/categorias', { method: 'POST', body: JSON.stringify({ nome: nome.trim() }) });
    setEnviando(false);
    if (r.ok) {
      toast.success(`Categoria "${nome.trim()}" criada.`);
      setNome('');
      invalidar();
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  const salvarEdicao = async (c, parcial) => {
    const payload = {
      nome: parcial.nome ?? c.nome,
      imagem: parcial.imagem !== undefined ? parcial.imagem : c.imagem,
      ativo: parcial.ativo ?? c.ativo,
      mostrarNaHome: parcial.mostrarNaHome ?? c.mostrarNaHome,
    };
    const r = await apiFetch(`/admin/categorias/${c.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (r.ok) {
      toast.success('Categoria atualizada.');
      invalidar();
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  const subirImagem = async (c, lista) => {
    if (!lista?.length) return;
    setSubindo(true);
    const fd = new FormData();
    fd.append('files', lista[0]);
    const r = await apiFetch('/admin/upload?tipo=logo', { method: 'POST', body: fd });
    setSubindo(false);
    if (r.ok) {
      const data = await r.json();
      await salvarEdicao(c, { imagem: data.files[0].url });
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const mover = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cats.length) return;
    const ids = cats.map((c) => c.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await apiFetch('/admin/categorias/ordenar', { method: 'POST', body: JSON.stringify({ ids }) });
    invalidar();
  };

  return (
    <div data-testid="admin-categorias" className="max-w-2xl">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Categorias</h1>
      <p className="mt-1 text-sm text-slate-500">Categorias ativas com "mostrar na home" aparecem nos cartões abaixo dos banners.</p>
      <form onSubmit={criar} className="mt-4 flex gap-2">
        <input
          data-testid="categoria-input"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nova categoria (ex.: Sofás)"
          className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-mk-blue"
        />
        <button data-testid="categoria-add-button" disabled={enviando} className="inline-flex items-center gap-2 rounded-full bg-mk-blue px-4 py-2.5 text-sm font-bold text-white hover:bg-mk-bluelight disabled:opacity-60">
          <PlusCircle size={15} /> Criar
        </button>
      </form>
      {isLoading ? (
        <p className="mt-6 text-sm text-slate-400">Carregando...</p>
      ) : (
        <div className="mt-4 space-y-2">
          {cats.map((c, i) => (
            <div key={c.id} data-testid={`categoria-row-${c.id}`} className={`rounded-2xl bg-white p-3.5 shadow-sm ${!c.ativo ? 'opacity-60' : ''}`}>
              <div className="flex flex-wrap items-center gap-3">
                {c.imagem ? (
                  <img src={c.imagem} alt="" className="h-11 w-11 rounded-full object-cover" />
                ) : (
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-mk-blue/10 text-mk-blue">
                    <IconeCategoria id={c.id} size={18} />
                  </span>
                )}
                {editando === c.id ? (
                  <input
                    data-testid={`categoria-nome-${c.id}`}
                    defaultValue={c.nome}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { salvarEdicao(c, { nome: e.target.value }); setEditando(null); } }}
                    className="min-w-0 flex-1 rounded-lg border border-mk-blue px-3 py-1.5 text-sm font-bold outline-none"
                  />
                ) : (
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{c.nome}</span>
                )}
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-extrabold text-slate-500">{c.total} produto(s)</span>
                <div className="flex items-center gap-1">
                  <button data-testid={`categoria-up-${c.id}`} onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Subir" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowUp size={15} /></button>
                  <button data-testid={`categoria-down-${c.id}`} onClick={() => mover(i, 1)} disabled={i === cats.length - 1} aria-label="Descer" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ArrowDown size={15} /></button>
                  {editando === c.id ? (
                    <>
                      <button data-testid={`categoria-save-${c.id}`} onClick={(e) => { const inp = e.currentTarget.closest('div').parentElement.querySelector('input'); salvarEdicao(c, { nome: inp.value }); setEditando(null); }} aria-label="Salvar" className="rounded-lg p-1.5 text-mk-green hover:bg-green-50"><Check size={15} /></button>
                      <button onClick={() => setEditando(null)} aria-label="Cancelar" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={15} /></button>
                    </>
                  ) : (
                    <button data-testid={`categoria-edit-${c.id}`} onClick={() => setEditando(c.id)} aria-label="Renomear" className="rounded-lg p-1.5 text-mk-blue hover:bg-blue-50"><Pencil size={15} /></button>
                  )}
                  <button
                    data-testid={`categoria-img-${c.id}`}
                    disabled={subindo}
                    onClick={() => { fileRef.current?.click(); fileRef.current.onchange = (e) => subirImagem(c, e.target.files); }}
                    aria-label="Enviar imagem"
                    className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                  >
                    <Upload size={15} />
                  </button>
                </div>
              </div>
              <div className="mt-2.5 flex flex-wrap gap-4 pl-1">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                  <input data-testid={`categoria-ativo-${c.id}`} type="checkbox" checked={c.ativo} onChange={(e) => salvarEdicao(c, { ativo: e.target.checked })} className="h-4 w-4 accent-mk-blue" />
                  Ativa no catálogo
                </label>
                <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                  <input data-testid={`categoria-home-${c.id}`} type="checkbox" checked={c.mostrarNaHome} onChange={(e) => salvarEdicao(c, { mostrarNaHome: e.target.checked })} className="h-4 w-4 accent-mk-blue" />
                  Mostrar na home
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden />
    </div>
  );
}
